import { Component, Input, OnDestroy, ElementRef, ViewChild, AfterViewInit, HostListener, ChangeDetectionStrategy } from '@angular/core';


@Component({
    selector: 'app-tooltip',
    standalone: true,
    imports: [],
    template: `
    <div 
      #trigger
      (mouseenter)="showTooltip()"
      (mouseleave)="hideTooltip()"
      class="">
      <ng-content></ng-content>
    </div>
    @if(isVisible){
    <div 
      #tooltipElement
      class="fixed z-[9999] bg-gray-900 text-white text-xs rounded py-1 px-2 whitespace-nowrap pointer-events-none shadow-lg transition-opacity duration-200"
      [style.left.px]="tooltipPosition.left"
      [style.top.px]="tooltipPosition.top"
      [class.opacity-100]="isPositioned"
      [class.opacity-0]="!isPositioned">
      {{ text }}
    </div>
    }
  `,
    changeDetection: ChangeDetectionStrategy.Eager,
    styles: []
})
export class TooltipComponent implements AfterViewInit, OnDestroy {
    @Input() text: string = '';
    @Input() position: 'left' | 'right' | 'top' | 'bottom' = 'left';

    @ViewChild('trigger', { static: false }) trigger!: ElementRef;
    @ViewChild('tooltipElement', { static: false }) tooltipElement?: ElementRef;

    isVisible = false;
    isPositioned = false;
    tooltipPosition = { left: 0, top: 0 };

    private isScrollListenerRegistered = false;
    private readonly onAnyScroll = () => {
        if (this.isVisible) {
            this.calculatePosition();
        }
    };

    ngAfterViewInit() { }

    @HostListener('window:resize')
    onWindowResize() {
        if (this.isVisible) {
            this.calculatePosition();
        }
    }

    showTooltip() {
        if (!this.text) return;

        // Rendre le tooltip visible dans le DOM mais visuellement masqué
        this.isVisible = true;
        this.isPositioned = false;

        if (!this.isScrollListenerRegistered) {
            window.addEventListener('scroll', this.onAnyScroll, true);
            this.isScrollListenerRegistered = true;
        }

        // Calculer la position puis rendre le tooltip visible
        setTimeout(() => {
            this.calculatePosition();
            // Montrer visuellement le tooltip une fois positionné
            this.isPositioned = true;
        }, 0);
    }

    hideTooltip() {
        this.isVisible = false;
        this.isPositioned = false;

        if (this.isScrollListenerRegistered) {
            window.removeEventListener('scroll', this.onAnyScroll, true);
            this.isScrollListenerRegistered = false;
        }
    }

    private calculatePosition() {
        if (!this.trigger || !this.tooltipElement) return;

        const triggerRect = this.trigger.nativeElement.getBoundingClientRect();
        const tooltipRect = this.tooltipElement.nativeElement.getBoundingClientRect();

        let left = 0;
        let top = 0;

        switch (this.position) {
            case 'left':
                left = triggerRect.left - tooltipRect.width - 8;
                top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
                break;
            case 'right':
                left = triggerRect.right + 8;
                top = triggerRect.top + (triggerRect.height / 2) - (tooltipRect.height / 2);
                break;
            case 'top':
                left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
                top = triggerRect.top - tooltipRect.height - 8;
                break;
            case 'bottom':
                left = triggerRect.left + (triggerRect.width / 2) - (tooltipRect.width / 2);
                top = triggerRect.bottom + 8;
                break;
        }

        const padding = 8;
        const maxLeft = Math.max(padding, window.innerWidth - tooltipRect.width - padding);
        const maxTop = Math.max(padding, window.innerHeight - tooltipRect.height - padding);

        left = Math.min(Math.max(left, padding), maxLeft);
        top = Math.min(Math.max(top, padding), maxTop);

        this.tooltipPosition = { left, top };
    }

    get getposition() {
        return this.tooltipPosition;
    }

    ngOnDestroy() {
        if (this.isScrollListenerRegistered) {
            window.removeEventListener('scroll', this.onAnyScroll, true);
            this.isScrollListenerRegistered = false;
        }
    }
}
